import workletUrl from './audioPlayerProcessor.worklet.js';

/**
 * AudioPlayer - Manages audio playback using Web Audio API with AudioWorklet
 * 
 * This class handles real-time audio streaming from the server, with features like:
 * - Queue-based playback to prevent audio stuttering
 * - Idle detection for session rotation
 * - Barge-in support for interruptions
 * 
 * Architecture:
 * - Main thread (this file): Manages audio context and worklet lifecycle
 * - Audio worklet thread: Handles actual audio processing in real-time
 */
export default class AudioPlayer {
  constructor() {
    // Audio API components
    this.initialized = false;
    this.audioContext = null;      // Web Audio API context
    this.workletNode = null;        // AudioWorklet node for processing
    this.analyser = null;           // Audio analyser (for future visualizations)
    
    // Idle detection system (used for session rotation)
    this._queuedSamples = 0;        // Number of audio samples waiting to play
    this._idleCbs = new Set();      // Callbacks to fire when audio finishes

    // Race-condition guard: incremented by stop() to invalidate any in-progress start().
    // This is critical in React StrictMode where effects run twice in development.
    this._startSequence = 0;
  }

  /**
   * Initialize the audio player
   * Sets up AudioContext, loads the worklet processor, and establishes communication
   */
  async start() {
    if (this.initialized) return;

    this._startSequence += 1;
    const startSequence = this._startSequence;

    try {
      // Create audio context with 24kHz sample rate (matches server output)
      this.audioContext = new AudioContext({ sampleRate: 24000 });
      
      // Create analyser node (useful for audio visualizations in the future)
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 512;

      // Load the audio worklet processor (runs in separate thread)
      await this.audioContext.audioWorklet.addModule(workletUrl);

      if (this._startSequence !== startSequence) return;

      this.workletNode = new AudioWorkletNode(this.audioContext, "audio-player-processor");

      // Listen to messages from the worklet thread
      // The worklet sends us updates about queue size and idle state
      this.workletNode.port.onmessage = (e) => {
        const msg = e?.data;
        if (!msg?.type) return;

        if (msg.type === "queue") {
          // Update local copy of queue size
          this._queuedSamples = Number(msg.queuedSamples) || 0;
          
          // If queue is empty, fire idle callbacks
          if (this._queuedSamples === 0) this._fireIdle();
        }

        if (msg.type === "idle") {
          // Worklet explicitly told us it's idle
          this._queuedSamples = 0;
          this._fireIdle();
        }
      };

      // Connect audio graph: workletNode -> analyser -> speakers
      this.workletNode.connect(this.analyser);
      this.analyser.connect(this.audioContext.destination);

      // Log any processing errors from the worklet
      this.workletNode.onprocessorerror = (err) => {
        console.error("AudioWorklet processing error:", err);
      };

      if (this._startSequence !== startSequence) {
        this.stop();
        return;
      }

      this.initialized = true;

      // Resume audio context if it's suspended (browser autoplay policy)
      if (this.audioContext.state !== 'running') {
        await this.audioContext.resume();
      }

      console.log("Audio player initialized successfully");
    } catch (error) {
      console.error("Failed to initialize audio player:", error);
      this.stop();
      throw error;
    }
  }

  /**
   * Fire all registered idle callbacks
   * This is called when the audio queue becomes empty
   * Used for session rotation timing
   */
  _fireIdle() {
    const cbs = Array.from(this._idleCbs);
    this._idleCbs.clear();
    
    for (const cb of cbs) {
      try { 
        cb(); 
      } catch (e) {
        console.warn("Idle callback error:", e);
      }
    }
  }

  /**
   * Check if the audio player is currently idle (nothing queued to play)
   * @returns {boolean} True if no audio is queued
   */
  isIdle() {
    return this._queuedSamples === 0;
  }

  /**
   * Register a callback to be called when audio playback finishes
   * Used for session rotation - we rotate after assistant finishes speaking
   * 
   * @param {Function} cb - Callback to execute when idle
   */
  onIdle(cb) {
    if (!this.initialized || typeof cb !== 'function') return;
    
    // If already idle, fire callback immediately
    if (this.isIdle()) {
      queueMicrotask(() => cb());
      return;
    }
    
    // Otherwise, queue it for when we become idle
    this._idleCbs.add(cb);
  }

  /**
   * Immediately clear all queued audio (barge-in)
   * Used when user interrupts the assistant
   */
  bargeIn() {
    if (!this.initialized) return;
    
    // Tell worklet to clear its buffer
    this.workletNode.port.postMessage({ type: "barge-in" });
    
    // Update local state and fire idle callbacks
    this._queuedSamples = 0;
    this._fireIdle();
  }

  /**
   * Stop and cleanup the audio player
   * Closes all audio resources
   */
  stop() {
    this._startSequence += 1;

    if (!this.initialized) return;

    try {
      // Disconnect and close all audio nodes
      if (this.audioContext) this.audioContext.close();
      if (this.analyser) this.analyser.disconnect();
      if (this.workletNode) this.workletNode.disconnect();
    } catch (e) {
      console.warn("Error during audio player cleanup:", e);
    }

    // Reset all state
    this.initialized = false;
    this.audioContext = null;
    this.analyser = null;
    this.workletNode = null;
    this._queuedSamples = 0;
    this._idleCbs.clear();
  }

  /**
   * Queue audio samples for playback
   * 
   * @param {Float32Array} samples - Audio samples to play (range: -1.0 to 1.0)
   */
  playAudio(samples) {
    if (!this.initialized) {
      console.error("Audio player not initialized. Call start() first.");
      return;
    }

    // Ignore empty audio chunks
    if (!samples || samples.length === 0) return;

    // Resume audio context if suspended (browser autoplay policy)
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume().catch(err => 
        console.error("Failed to resume audio context:", err)
      );
    }

    // Send audio data to worklet for processing
    // The worklet will queue and play it in the audio thread
    this.workletNode.port.postMessage({
      type: "audio",
      audioData: samples,
    });
  }
}